import api from 'api'
import { get } from 'utils/lodash'

import { CACHE_TAG_ROUTES, CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS } from '../cacheTagTypes'

type Stop = Record<string, unknown>
type StopMatcher = (stop: Stop) => boolean
type NoteCountField = 'workOrderNoteCount' | 'locationNoteCount' | 'configuredServiceNoteCount'
type PatchResult = { undo: () => void }

const ROUTE_STOP_ENDPOINTS = ['getRouteStops', 'getRouteStopsUnassigned', 'getDailyLane']

type CacheEntry = { endpointName: string; originalArgs: unknown }

// Endpoints are injected from a dozen modules, so the base api's endpoint-name union is empty
// and these helpers type their names as `never`. Widen once here instead of at every call.
const cacheUtil = api.util as unknown as {
  selectInvalidatedBy: (state: unknown, tags: string[]) => CacheEntry[]
  updateQueryData: (endpointName: string, args: unknown, recipe: (draft: unknown) => void) => unknown
}

// Each cached shape keeps its stops somewhere else: the bulk list under routes, Unassigned flat, a lane in pages.
const forEachStop = (draft: unknown, visit: (stop: Stop) => void) => {
  get(draft, 'stops', []).forEach(visit)
  get(draft, 'routes', []).forEach((route: unknown) => get(route, 'stops', []).forEach(visit))
  get(draft, 'pages', []).forEach((page: unknown) => get(page, 'stops', []).forEach(visit))
}

type MutationLifecycle = {
  dispatch: (action: unknown) => PatchResult
  getState: () => unknown
  queryFulfilled: Promise<unknown>
}

type PatchArgs = {
  dispatch: (action: unknown) => PatchResult
  getState: () => unknown
  // A location or service note can back several stops on the board, and every one of their
  // badges counts it — so this matches stops rather than resolving a single one.
  matches: StopMatcher
  field: NoteCountField
  delta?: number
}

/**
 * Move the board's note badges the moment a note is written, rather than making the user wait
 * for the CACHE_TAG_ROUTES invalidation to refetch the whole day's routes. The invalidation
 * still runs and reconciles the real numbers; this only hides its latency.
 *
 * Returns the patch handles so a failed write can undo them.
 */
export const patchStopNoteCounts = ({ dispatch, getState, matches, field, delta = 1 }: PatchArgs): PatchResult[] => {
  const entries = cacheUtil.selectInvalidatedBy(getState(), [CACHE_TAG_ROUTES, CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS])

  return entries
    .filter(({ endpointName }) => ROUTE_STOP_ENDPOINTS.includes(endpointName))
    .map(({ endpointName, originalArgs }) =>
      dispatch(
        cacheUtil.updateQueryData(endpointName, originalArgs, draft => {
          forEachStop(draft, stop => {
            if (!matches(stop)) return
            // Mutating the immer draft rather than rebuilding it is deliberate: getRouteStops
            // mirrors each route object in `routesById`, and immer proxies a shared reference
            // once, so this one write reaches both views. Rebuilding `routes` immutably would
            // leave `routesById` pointing at the old objects with stale counts.
            // eslint-disable-next-line no-param-reassign
            stop[field] = (Number(stop[field]) || 0) + delta
          })
        })
      )
    )
}

/** Wraps a note-create mutation's lifecycle: patch now, undo if the write fails. */
export const optimisticNoteCreate =
  (field: NoteCountField, matches: (id: string) => StopMatcher) =>
  async ({ id }: { id: string }, { dispatch, getState, queryFulfilled }: MutationLifecycle) => {
    const patches = patchStopNoteCounts({ dispatch, getState, matches: matches(id), field })
    try {
      await queryFulfilled
    } catch {
      patches.forEach(patch => patch.undo())
    }
  }
